import React from 'react';

import { useEffect, useMemo, useState } from 'react';
import { Line, Pie, Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import fetchJson from '../hooks/fetchJson';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/Loader';
import { useExchangeRate } from '../ExchangeRateContext';

const Dashboard = () => {
  const { user, token } = useAuth();
  const { rate } = useExchangeRate();
  const [frame, setFrame] = useState('daily'); // 'daily' | 'weekly' | 'monthly' | ''
  const [currency, setCurrency] = useState('UZS'); // 'UZS' | 'USD'
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(''); // YYYY-MM-DD
  const [endDate, setEndDate] = useState('');   // YYYY-MM-DD
  // Cards (unified)
  const [totals, setTotals] = useState({ totalUZS: 0, totalUSD: 0 });
  const [retailTotals, setRetailTotals] = useState({ totalUZS: 0, totalUSD: 0 });
  const [loadingCards, setLoadingCards] = useState(false);
  const [errorCards, setErrorCards] = useState('');
  // Sales amounts states (separate controls)
  const [frameSales, setFrameSales] = useState('daily');
  const [startSales, setStartSales] = useState('');
  const [endSales, setEndSales] = useState('');
  const [seriesSales, setSeriesSales] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [errorSales, setErrorSales] = useState('');
  const [currencySales, setCurrencySales] = useState(''); // '' (count) | 'UZS' | 'USD'
  // Products count card
  const [productsCount, setProductsCount] = useState(0);
  const [productsCountFIFO, setProductsCountFIFO] = useState(0);
  const [productsCountSpecific, setProductsCountSpecific] = useState(0);
  // Customer2 payments pie
  const [c2Pie, setC2Pie] = useState({ total_payments: 0, total_unpaid: 0, missing_payments: 0 });
  const [loadingC2Pie, setLoadingC2Pie] = useState(false);
  const [errorC2Pie, setErrorC2Pie] = useState('');
  // Best-selling products (bar chart)
  const [frameBest, setFrameBest] = useState('daily');
  const [startBest, setStartBest] = useState('');
  const [endBest, setEndBest] = useState('');
  const [limitBest, setLimitBest] = useState(5);
  const [itemsBest, setItemsBest] = useState([]);
  const [loadingBest, setLoadingBest] = useState(false);
  const [errorBest, setErrorBest] = useState('');
  // Selected product series (quantity sold over time)
  const [selectedProductId, setSelectedProductId] = useState('');
  const [frameProd, setFrameProd] = useState('daily');
  const [startProd, setStartProd] = useState('');
  const [endProd, setEndProd] = useState('');
  const [seriesProd, setSeriesProd] = useState([]);
  const [loadingProd, setLoadingProd] = useState(false);
  const [errorProd, setErrorProd] = useState('');

  const headers = useMemo(() => ({
    ...(user?.ownerId ? { 'x-owner-id': String(user.ownerId) } : {}),
    ...(user?.staffId ? { 'x-staff-id': String(user.staffId) } : {}),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  }), [user, token]);

  // Derived overall amounts (converted to UZS) and Marja % (chakana)
  const overallCostUZS = useMemo(() => {
    const usdToUzs = rate ? (totals.totalUSD * rate) : 0;
    return totals.totalUZS + usdToUzs;
  }, [totals, rate]);

  const overallSaleUZS = useMemo(() => {
    const usdToUzs = rate ? (retailTotals.totalUSD * rate) : 0;
    return retailTotals.totalUZS + usdToUzs;
  }, [retailTotals, rate]);

  const marjaPercent = useMemo(() => {
    const cost = overallCostUZS;
    const sale = overallSaleUZS;
    if (!cost || cost <= 0) return 0;
    return ((sale * 100) / cost) - 100;
  }, [overallCostUZS, overallSaleUZS]);

  // Load all cards in one request
  useEffect(() => {
    const loadCards = async () => {
      setLoadingCards(true);
      setErrorCards('');
      const { data, error } = await fetchJson('/api/statistics/cards', { headers });
      if (error) {
        setErrorCards(error);
        setTotals({ totalUZS: 0, totalUSD: 0 });
        setRetailTotals({ totalUZS: 0, totalUSD: 0 });
        setProductsCount(0);
        setProductsCountFIFO(0);
        setProductsCountSpecific(0);
      } else {
        const t = data?.totals || {};
        setTotals({
          totalUZS: Number(t.totalUZS || 0),
          totalUSD: Number(t.totalUSD || 0),
        });
        const rt = data?.retailTotals || {};
        setRetailTotals({
          totalUZS: Number(rt.totalUZS || 0),
          totalUSD: Number(rt.totalUSD || 0),
        });
        setProductsCount(Number(data?.productsCount || 0));
        setProductsCountFIFO(Number(data?.productsCountFIFO || 0));
        setProductsCountSpecific(Number(data?.productsCountSpecific || 0));
      }
      setLoadingCards(false);
    };
    loadCards();
  }, [headers]);

  // Load best-selling products
  useEffect(() => {
    const load = async () => {
      setLoadingBest(true);
      setErrorBest('');
      let url = '';
      if (startBest && endBest) {
        url = `/api/statistics/best-selling?startDate=${encodeURIComponent(startBest)}&endDate=${encodeURIComponent(endBest)}&limit=${encodeURIComponent(limitBest)}`;
      } else if (frameBest) {
        url = `/api/statistics/best-selling?frame=${encodeURIComponent(frameBest)}&limit=${encodeURIComponent(limitBest)}`;
      } else {
        setItemsBest([]);
        setLoadingBest(false);
        return;
      }
      const { data, error } = await fetchJson(url, { headers });
      if (error) {
        setErrorBest(error);
        setItemsBest([]);
      } else {
        const items = Array.isArray(data?.items) ? data.items : [];
        setItemsBest(items);
        // Auto-select first product if none selected
        if (!selectedProductId && items.length > 0) {
          setSelectedProductId(String(items[0].productid));
        }
      }
      setLoadingBest(false);
    };
    load();
  }, [frameBest, startBest, endBest, limitBest, headers]);

  const bestLabels = useMemo(() => itemsBest.map(i => i.name), [itemsBest]);
  const bestData = useMemo(() => ({
    labels: bestLabels,
    datasets: [
      {
        label: 'Sotilgan miqdor',
        data: itemsBest.map(i => Number(i.total_quantity || 0)),
        backgroundColor: '#20c997',
        borderColor: '#198754',
        borderWidth: 1,
      },
    ],
  }), [bestLabels, itemsBest]);

  // Load selected product sales series
  useEffect(() => {
    const load = async () => {
      if (!selectedProductId) {
        setSeriesProd([]);
        return;
      }
      setLoadingProd(true);
      setErrorProd('');
      let url = '';
      if (startProd && endProd) {
        url = `/api/statistics/product-sales-series/${encodeURIComponent(selectedProductId)}?startDate=${encodeURIComponent(startProd)}&endDate=${encodeURIComponent(endProd)}`;
      } else if (frameProd) {
        url = `/api/statistics/product-sales-series/${encodeURIComponent(selectedProductId)}?frame=${encodeURIComponent(frameProd)}`;
      } else {
        setSeriesProd([]);
        setLoadingProd(false);
        return;
      }
      const { data, error } = await fetchJson(url, { headers });
      if (error) {
        setErrorProd(error);
        setSeriesProd([]);
      } else {
        setSeriesProd(Array.isArray(data?.series) ? data.series : []);
      }
      setLoadingProd(false);
    };
    load();
  }, [selectedProductId, frameProd, startProd, endProd, headers]);

  const labelsProd = useMemo(() => {
    const pad = (n) => String(n).padStart(2, '0');
    return seriesProd.map((item) => {
      const d = new Date(item.bucket);
      const y = d.getFullYear();
      const m = pad(d.getMonth() + 1);
      const day = pad(d.getDate());
      if (!startProd && !endProd && frameProd === 'daily') {
        const hh = pad(d.getHours());
        return `${hh}:00`;
      }
      return `${y}-${m}-${day}`;
    });
  }, [seriesProd, frameProd, startProd, endProd]);

  const chartDataProd = useMemo(() => ({
    labels: labelsProd,
    datasets: [
      {
        label: 'Miqdor',
        data: seriesProd.map((i) => Number(i.total || 0)),
        borderColor: '#6610f2',
        backgroundColor: 'rgba(102, 16, 242, 0.15)',
        tension: 0.3,
        fill: true,
        pointRadius: 2,
      },
    ],
  }), [labelsProd, seriesProd]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      let url = '';
      // Use date range if both chosen; else use frame
      if (startDate && endDate) {
        url = `/api/statistics/profits?currency=${encodeURIComponent(currency)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
      } else if (frame) {
        url = `/api/statistics/profits?frame=${encodeURIComponent(frame)}&currency=${encodeURIComponent(currency)}`;
      } else {
        setSeries([]);
        setLoading(false);
        return;
      }
      const { data, error } = await fetchJson(url, { headers });
      if (error) {
        setError(error);
        setSeries([]);
      } else {
        setSeries(Array.isArray(data?.series) ? data.series : []);
      }
      setLoading(false);
    };
    load();
  }, [frame, currency, startDate, endDate, headers]);

  const labels = useMemo(() => {
    const pad = (n) => String(n).padStart(2, '0');
    return series.map((item) => {
      const d = new Date(item.bucket);
      const y = d.getFullYear();
      const m = pad(d.getMonth() + 1);
      const day = pad(d.getDate());
      // Hourly labels for daily frame (today)
      if (!startDate && !endDate && frame === 'daily') {
        const hh = pad(d.getHours());
        return `${hh}:00`;
      }
      // Daily labels (YYYY-MM-DD) for weekly, monthly, or date range
      return `${y}-${m}-${day}`;
    });
  }, [series, frame, startDate, endDate]);

  const chartData = useMemo(() => ({
    labels,
    datasets: [
      {
        label: `Foyda (${currency})`,
        data: series.map((i) => Number(i.total || 0)),
        borderColor: '#198754',
        backgroundColor: 'rgba(25,135,84,0.15)',
        tension: 0.2,
        fill: true,
        pointRadius: 3,
      },
    ],
  }), [labels, series, currency]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: true },
      tooltip: { enabled: true },
    },
    scales: {
      y: { beginAtZero: true },
    },
  }), []);

  // Load sales amounts series
  useEffect(() => {
    const load = async () => {
      setLoadingSales(true);
      setErrorSales('');
      let url = '';
      if (startSales && endSales) {
        url = `/api/statistics/sales-amounts?startDate=${encodeURIComponent(startSales)}&endDate=${encodeURIComponent(endSales)}&currency=${encodeURIComponent(currencySales)}`;
      } else if (frameSales) {
        url = `/api/statistics/sales-amounts?frame=${encodeURIComponent(frameSales)}&currency=${encodeURIComponent(currencySales)}`;
      } else {
        setSeriesSales([]);
        setLoadingSales(false);
        return;
      }
      const { data, error } = await fetchJson(url, { headers });
      if (error) {
        setErrorSales(error);
        setSeriesSales([]);
      } else {
        setSeriesSales(Array.isArray(data?.series) ? data.series : []);
      }
      setLoadingSales(false);
    };
    load();
  }, [frameSales, startSales, endSales, currencySales, headers]);

  const labelsSales = useMemo(() => {
    const pad = (n) => String(n).padStart(2, '0');
    return seriesSales.map((item) => {
      const d = new Date(item.bucket);
      const y = d.getFullYear();
      const m = pad(d.getMonth() + 1);
      const day = pad(d.getDate());
      if (!startSales && !endSales && frameSales === 'daily') {
        const hh = pad(d.getHours());
        return `${hh}:00`;
      }
      return `${y}-${m}-${day}`;
    });
  }, [seriesSales, frameSales, startSales, endSales]);

  const chartDataSales = useMemo(() => ({
    labels: labelsSales,
    datasets: [
      {
        label: `Sotuvlar ${currencySales === 'UZS' || currencySales === 'USD' ? `(${currencySales})` : 'soni'}`,
        data: seriesSales.map((i) => Number(i.total || 0)),
        borderColor: '#0d6efd',
        backgroundColor: 'rgba(13, 97, 253, 0.15)',
        tension: 0.4,
        fill: true,
        pointRadius: 2,
      },
    ],
  }), [labelsSales, seriesSales, currencySales]);

  const totalProfit = useMemo(() => series.reduce((sum, item) => sum + Number(item.total || 0), 0), [series]);
  const totalSales = useMemo(() => seriesSales.reduce((sum, item) => sum + Number(item.total || 0), 0), [seriesSales]);

  // Load Customer2 payments pie
  useEffect(() => {
    const load = async () => {
      setLoadingC2Pie(true);
      setErrorC2Pie('');
      const { data, error } = await fetchJson('/api/statistics/customer2/payments-pie', { headers });
      if (error) {
        setErrorC2Pie(error);
        setC2Pie({ total_payments: 0, total_unpaid: 0, missing_payments: 0 });
      } else {
        setC2Pie({
          total_payments: Number(data?.total_payments || 0),
          total_unpaid: Number(data?.total_unpaid || 0),
          missing_payments: Number(data?.missing_payments || 0),
        });
      }
      setLoadingC2Pie(false);
    };
    load();
  }, [headers]);

  const pieData = useMemo(() => ({
    labels: ['To`langan', 'Kutilayotgan', 'Kechiktirilgan'],
    datasets: [
      {
        data: [c2Pie.total_payments, c2Pie.total_unpaid, c2Pie.missing_payments],
        backgroundColor: ['#198754', '#ffc107', '#dc3545'],
        borderColor: '#fff',      // border color (white works well)
        borderWidth: 1
      },
    ],
  }), [c2Pie]);

  const pieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const val = Number(ctx.parsed) || 0;
            return `${ctx.label}: ${val.toLocaleString(undefined, { maximumFractionDigits: 2 })} UZS`;
          },
        },
      },
    },
  }), []);

  return (
    <div className="container mt-4">
      {/* Top blue card: Purchased products total cost */}
      <div className="w-100 d-flex flex-column flex-md-row justify-content-between gap-2">

        <div className="card bg-primary text-white shadow border-0 mb-4 w-100 w-md-25">
          <div className="card-body">
            <h2 className="h5 mb-3">Umumiy tovar narxi</h2>
            {loadingCards ? (
              <Loader />
            ) : errorCards ? (
              <div className="alert alert-light text-danger mb-0">{errorCards}</div>
            ) : (
              <div className="row g-1">
                <div className="">
                  <div className="d-flex justify-content-between">
                    <strong>{totals.totalUZS.toLocaleString(undefined, { maximumFractionDigits: 2 })} UZS</strong>
                  </div>
                </div>
                <div className="">
                  <div className="d-flex justify-content-between">
                    <strong>{totals.totalUSD.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD</strong>
                  </div>
                </div>
                <div className="">
                  <div className="d-flex justify-content-between">
                    <strong>
                      ≈ {(totals.totalUZS + (rate ? (totals.totalUSD * rate) : 0)).toLocaleString(undefined, { maximumFractionDigits: 2 })} UZS
                    </strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Retail price sum card */}
        <div className="card bg-warning text-dark shadow border-0 mb-4 w-100 w-md-25">
          <div className="card-body" style={{ minHeight: 120 }}>
            <h2 className="h6 mb-3">Sotiladigan narx (Chakana)</h2>
            {loadingCards ? (
              <Loader />
            ) : errorCards ? (
              <div className="alert alert-light text-danger mb-0">{errorCards}</div>
            ) : (
              <div className="row g-1">
                <div className="">
                  <div className="d-flex justify-content-between">
                    <strong>{retailTotals.totalUZS.toLocaleString(undefined, { maximumFractionDigits: 2 })} UZS</strong>
                  </div>
                </div>
                <div className="">
                  <div className="d-flex justify-content-between">
                    <strong>{retailTotals.totalUSD.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD</strong>
                  </div>
                </div>
                <div className="">
                  <div className="d-flex justify-content-between">
                    <strong>
                      ≈ {(retailTotals.totalUZS + (rate ? (retailTotals.totalUSD * rate) : 0)).toLocaleString(undefined, { maximumFractionDigits: 2 })} UZS
                    </strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Marja % (chakana) card */}
        <div className="card bg-success text-white shadow border-0 mb-4 w-100 w-md-25">
          <div className="card-body d-flex align-items-center justify-content-center" style={{ minHeight: 120 }}>
            {loadingCards ? (
              <Loader />
            ) : errorCards ? (
              <div className="alert alert-light text-danger mb-0">{errorCards}</div>
            ) : (
              <div className="text-center">
                <div className="small">Marja % (chakana)</div>
                <div className="display-6 fw-bold">
                  {marjaPercent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Products count card */}
        <div className="card bg-info text-white shadow border-0 mb-4 w-100 w-md-25">
          <div className="card-body position-relative d-flex align-items-center justify-content-center" style={{ height: 120 }}>
            {loadingCards ? (
              <Loader />
            ) : errorCards ? (
              <div className="alert alert-light text-danger mb-0">{errorCards}</div>
            ) : (
              <>
                <div className="text-center">
                  <div className="small">Mahsulotlar soni</div>
                  <div className="display-6 fw-bold">{productsCount.toLocaleString()}</div>
                </div>
                <div className="position-absolute bottom-0 start-0 m-2">
                  <span className="badge rounded-pill" style={{ backgroundColor: 'aqua', color: '#000' }}>
                    FIFO: {productsCountFIFO.toLocaleString()}
                  </span>
                </div>
                <div className="position-absolute bottom-0 end-0 m-2">
                  <span className="badge rounded-pill" style={{ backgroundColor: 'aqua', color: '#000' }}>
                    Specific: {productsCountSpecific.toLocaleString()}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="row align-items-center mb-3">
        <div className="col">
          <h1 className="h4 mb-0">Profit: {totalProfit.toLocaleString(undefined, { maximumFractionDigits: 2 })} {currency}</h1>
        </div>
        <div className="col-auto">
          <div className="row g-2">
            <div className="col">
              <select
                className="form-select"
                value={frame}
                onChange={(e) => {
                  const val = e.target.value;
                  setFrame(val);
                  // Clear date inputs when choosing a timeframe
                  setStartDate('');
                  setEndDate('');
                }}
              >
                <option value="daily">Kun</option>
                <option value="weekly">Hafta</option>
                <option value="monthly">Oy</option>
              </select>
            </div>
            <div className="col">
              <select className="form-select" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                <option value="UZS">UZS</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-12 col-md-3">
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => {
              const val = e.target.value;
              setStartDate(val);
              // If both dates selected, clear timeframe selection
              if (val && endDate) setFrame('');
            }}
          />
        </div>
        <div className="col-12 col-md-3">
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => {
              const val = e.target.value;
              setEndDate(val);
              if (startDate && val) setFrame('');
            }}
          />
        </div>
        {(startDate || endDate) && (
          <div className="col-auto d-flex align-items-center">
            <button
              className="btn btn-outline-secondary"
              onClick={() => { setStartDate(''); setEndDate(''); }}
              type="button"
            >
              Clear dates
            </button>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">Foyda</div>
        <div className="card-body" style={{ height: 360 }}>
          {loading ? (
            <Loader />
          ) : error ? (
            <div className="alert alert-danger mb-0">{error}</div>
          ) : (
            <Line data={chartData} options={options} />
          )}
        </div>
      </div>

      <div className="row align-items-center mt-4 mb-2">
        <div className="col">
          <h2 className="h5 mb-0">Sotuvlar {currencySales === 'UZS' || currencySales === 'USD' ? `(${currencySales})` : 'soni'}: {totalSales.toLocaleString(undefined, { maximumFractionDigits: 2 })}</h2>
        </div>
        <div className="col-auto">
          <div className="row g-2">
            <div className="col">
              <select
                className="form-select"
                value={frameSales}
                onChange={(e) => {
                  const val = e.target.value;
                  setFrameSales(val);
                  setStartSales('');
                  setEndSales('');
                }}
              >
                <option value="daily">Kun</option>
                <option value="weekly">Hafta</option>
                <option value="monthly">Oy</option>
              </select>
            </div>
            <div className="col">
              <select className="form-select" value={currencySales} onChange={(e) => setCurrencySales(e.target.value)}>
                <option value="">Soni (Count)</option>
                <option value="UZS">UZS</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-12 col-md-3">
          <input
            type="date"
            className="form-control"
            value={startSales}
            onChange={(e) => {
              const val = e.target.value;
              setStartSales(val);
              if (val && endSales) setFrameSales('');
            }}
          />
        </div>
        <div className="col-12 col-md-3">
          <input
            type="date"
            className="form-control"
            value={endSales}
            onChange={(e) => {
              const val = e.target.value;
              setEndSales(val);
              if (startSales && val) setFrameSales('');
            }}
          />
        </div>
        {(startSales || endSales) && (
          <div className="col-auto d-flex align-items-center">
            <button
              className="btn btn-outline-secondary"
              onClick={() => { setStartSales(''); setEndSales(''); }}
              type="button"
            >
              Clear dates
            </button>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">Sotuvlar</div>
        <div className="card-body" style={{ height: 360 }}>
          {loadingSales ? (
            <Loader />
          ) : errorSales ? (
            <div className="alert alert-danger mb-0">{errorSales}</div>
          ) : (
            <Line data={chartDataSales} options={options} />
          )}
        </div>
      </div>

      {/* Best-selling products */}
      <div className="row align-items-center mt-4 mb-2">
        <div className="col">
          <h2 className="h5 mb-0">Eng ko'p sotilgan mahsulotlar</h2>
        </div>
        <div className="col-auto">
          <div className="row g-2">
            <div className="col">
              <select
                className="form-select"
                value={frameBest}
                onChange={(e) => {
                  const val = e.target.value;
                  setFrameBest(val);
                  setStartBest('');
                  setEndBest('');
                }}
              >
                <option value="daily">Kun</option>
                <option value="weekly">Hafta</option>
                <option value="monthly">Oy</option>
              </select>
            </div>
            <div className="col">
              <input
                type="number"
                min={1}
                className="form-control"
                value={limitBest}
                onChange={(e) => setLimitBest(Math.max(1, Number(e.target.value) || 5))}
                placeholder="Soni (default 5)"
                title="Ko'rsatiladigan mahsulotlar soni"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-12 col-md-3">
          <input
            type="date"
            className="form-control"
            value={startBest}
            onChange={(e) => {
              const val = e.target.value;
              setStartBest(val);
              if (val && endBest) setFrameBest('');
            }}
          />
        </div>
        <div className="col-12 col-md-3">
          <input
            type="date"
            className="form-control"
            value={endBest}
            onChange={(e) => {
              const val = e.target.value;
              setEndBest(val);
              if (startBest && val) setFrameBest('');
            }}
          />
        </div>
        {(startBest || endBest) && (
          <div className="col-auto d-flex align-items-center">
            <button
              className="btn btn-outline-secondary"
              onClick={() => { setStartBest(''); setEndBest(''); }}
              type="button"
            >
              Clear dates
            </button>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">Eng ko'p sotilgan mahsulotlar</div>
        <div className="card-body" style={{ height: 360 }}>
          {loadingBest ? (
            <Loader />
          ) : errorBest ? (
            <div className="alert alert-danger mb-0">{errorBest}</div>
          ) : (
            <Bar data={bestData} options={{ responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } }} />
          )}
        </div>
      </div>

      {/* Selected product sales series */}
      <div className="row align-items-center mt-4 mb-2">
        <div className="col">
          <h2 className="h5 mb-0">Tanlangan mahsulot sotuvlari</h2>
        </div>
        <div className="col-auto">
          <div className="row g-2">
            <div className="col">
              <select
                className="form-select"
                value={frameProd}
                onChange={(e) => {
                  const val = e.target.value;
                  setFrameProd(val);
                  setStartProd('');
                  setEndProd('');
                }}
              >
                <option value="daily">Kun</option>
                <option value="weekly">Hafta</option>
                <option value="monthly">Oy</option>
              </select>
            </div>
            <div className="col">
              <select
                className="form-select"
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
              >
                <option value="">Mahsulotni tanlang...</option>
                {itemsBest.map((p) => (
                  <option key={p.productid} value={p.productid}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-12 col-md-3">
          <input
            type="date"
            className="form-control"
            value={startProd}
            onChange={(e) => {
              const val = e.target.value;
              setStartProd(val);
              if (val && endProd) setFrameProd('');
            }}
          />
        </div>
        <div className="col-12 col-md-3">
          <input
            type="date"
            className="form-control"
            value={endProd}
            onChange={(e) => {
              const val = e.target.value;
              setEndProd(val);
              if (startProd && val) setFrameProd('');
            }}
          />
        </div>
        {(startProd || endProd) && (
          <div className="col-auto d-flex align-items-center">
            <button
              className="btn btn-outline-secondary"
              onClick={() => { setStartProd(''); setEndProd(''); }}
              type="button"
            >
              Clear dates
            </button>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">Tanlangan mahsulot sotuvlari</div>
        <div className="card-body" style={{ height: 360 }}>
          {loadingProd ? (
            <Loader />
          ) : errorProd ? (
            <div className="alert alert-danger mb-0">{errorProd}</div>
          ) : selectedProductId ? (
            <Line data={chartDataProd} options={options} />
          ) : (
            <div className="text-muted">Mahsulot tanlang.</div>
          )}
        </div>
      </div>

      {/* Customer2 payments pie */}
      <div className="card mt-4">
        <div className="card-header">Qarz (kredit) mijozlar</div>
        <div className="card-body" >
          {loadingC2Pie ? (
            <Loader />
          ) : errorC2Pie ? (
            <div className="alert alert-danger mb-0">{errorC2Pie}</div>
          ) : (
            <div className="row">
              <div className="col-12 col-md-6">
                <Pie data={pieData} options={pieOptions} />
              </div>
              <div className="col-12 col-md-6 d-flex flex-column justify-content-center">
                <ul className="list-group">
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    <span>To'langan</span>
                    <span className="badge bg-success">{c2Pie.total_payments.toLocaleString(undefined, { maximumFractionDigits: 2 })} UZS</span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    <span>Kutilayotgan</span>
                    <span className="badge bg-warning text-dark">{c2Pie.total_unpaid.toLocaleString(undefined, { maximumFractionDigits: 2 })} UZS</span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    <span>Kechktirilgan</span>
                    <span className="badge bg-danger">{c2Pie.missing_payments.toLocaleString(undefined, { maximumFractionDigits: 2 })} UZS</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;